import React, { useState, useRef } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Box,
} from "@mui/material";
import { Calendar } from "lucide-react";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import { handleDateChange, validateDate } from "../utils/dateValidation";

dayjs.extend(customParseFormat);
dayjs.extend(localeData);

const DatePickerField = ({
  id,
  name,
  placeholder = "DD/MM/YYYY",
  value,
  onChange,
  onError,
  error,
  helperText,
  required = false,
  allowFutureDates = false,
  sx = {},
  ...props
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [currentView, setCurrentView] = useState("day");
  const inputRef = useRef(null);

  const handleTextChange = (e) => {
    handleDateChange(e.target.value, onChange, onError, allowFutureDates);
  };

  const handleDatePickerChange = (date) => {
    if (date && dayjs(date).isValid()) {
      const formattedDate = dayjs(date).format("DD/MM/YYYY");
      onChange(formattedDate);

      // Validate the selected date
      if (onError) {
        const validation = validateDate(formattedDate, allowFutureDates);
        onError(validation.error);
      }

      // Only close the picker when on day view (final selection)
      if (currentView === "day") {
        setIsPickerOpen(false);
        setCurrentView("day");
      }
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleCalendarClick = (e) => {
    e.preventDefault();
    setIsPickerOpen(true);
  };

  // Convert DD/MM/YYYY to dayjs object for date picker
  const dateValue =
    value && value.length === 10 ? dayjs(value, "DD/MM/YYYY", true) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TextField
        ref={inputRef}
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={handleTextChange}
        fullWidth
        variant="outlined"
        required={required}
        error={error}
        helperText={helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleCalendarClick} edge="end">
                <Calendar size={20} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          backgroundColor: "white",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
          },
          ...sx,
        }}
        {...props}
      />

      <Popover
        open={isPickerOpen}
        anchorEl={inputRef.current}
        onClose={() => setIsPickerOpen(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            marginTop: 1,
            boxShadow: 3,
          },
        }}
      >
        <Box>
          <StaticDatePicker
            value={dateValue}
            onChange={handleDatePickerChange}
            onViewChange={handleViewChange}
            maxDate={allowFutureDates ? undefined : dayjs()}
            displayStaticWrapperAs="desktop"
            openTo="day"
            views={["year", "month", "day"]}
            slotProps={{
              actionBar: {
                actions: [],
              },
              layout: {
                sx: {
                  "& .MuiPickersDay-today": {
                    borderColor: "#FFB60C",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#FFB60C !important",
                    color: "#000",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "#FFB60C !important",
                  },
                  "& .MuiPickersCalendarHeader-root": {
                    paddingLeft: 2,
                    paddingRight: 2,
                  },
                  "& .MuiDayCalendar-root": {
                    margin: 0,
                  },
                },
              },
            }}
          />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DatePickerField;
